/**
 * Firebase Google sign-in — the only identity provider (PRD A1).
 *
 * The flow: Google popup → Firebase ID token → `POST /api/v1/auth/session`, where the
 * server verifies the token against Google's JWKS, enforces the `@botpresso.com`
 * domain and issues the httpOnly session cookie. This module's whole job is to hand
 * back that ID token; it never stores it and never talks to `/api/v1` itself.
 *
 * Dev, CI and unit tests run without any `VITE_FIREBASE_*` variables. Importing this
 * module in that state must not throw — callers read {@link firebaseConfigured} and
 * render the "sign-in unavailable" path instead.
 */
import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  GoogleAuthProvider,
  getAuth,
  signInWithPopup,
  signOut as firebaseSignOut,
  type Auth,
} from "firebase/auth";

interface FirebaseEnv {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
  storageBucket?: string;
  messagingSenderId?: string;
}

const env = import.meta.env;

const rawConfig = {
  apiKey: env.VITE_FIREBASE_API_KEY,
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: env.VITE_FIREBASE_PROJECT_ID,
  appId: env.VITE_FIREBASE_APP_ID,
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
};

const REQUIRED_KEYS = ["apiKey", "authDomain", "projectId", "appId"] as const;

export const missingFirebaseKeys: string[] = REQUIRED_KEYS.filter((key) => !rawConfig[key]).map(
  (key) => `VITE_FIREBASE_${key.replace(/[A-Z]/g, (c) => `_${c}`).toUpperCase()}`,
);

/** False in dev/CI when the env vars are absent. Never throws either way. */
export const firebaseConfigured: boolean = missingFirebaseKeys.length === 0;

const firebaseConfig: FirebaseEnv | null = firebaseConfigured
  ? (rawConfig as FirebaseEnv)
  : null;

let app: FirebaseApp | null = null;
let auth: Auth | null = null;

/** Lazily created so an unconfigured app never initialises the SDK at import time. */
function getFirebaseAuth(): Auth {
  if (!firebaseConfig) {
    throw new FirebaseNotConfiguredError(missingFirebaseKeys);
  }
  if (!app) app = initializeApp(firebaseConfig);
  if (!auth) auth = getAuth(app);
  return auth;
}

export class FirebaseNotConfiguredError extends Error {
  override readonly name = "FirebaseNotConfiguredError";
  readonly missing: string[];

  constructor(missing: string[]) {
    super(
      `Google sign-in is not configured. Missing environment ${
        missing.length === 1 ? "variable" : "variables"
      }: ${missing.join(", ")}.`,
    );
    this.missing = missing;
  }
}

/** Thrown when the user closes the popup — callers treat it as "no decision made". */
export function isSignInCancelled(error: unknown): boolean {
  const code = (error as { code?: string } | null)?.code;
  return (
    code === "auth/popup-closed-by-user" ||
    code === "auth/cancelled-popup-request" ||
    code === "auth/user-cancelled"
  );
}

export interface GoogleSignInResult {
  idToken: string;
  email: string | null;
  displayName: string | null;
}

/**
 * Opens the Google popup and returns the freshly minted ID token.
 * Domain restriction is advisory here (`hd` hint) and enforced on the server.
 */
export async function signInWithGoogle(): Promise<GoogleSignInResult> {
  const firebaseAuth = getFirebaseAuth();
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ hd: "botpresso.com", prompt: "select_account" });

  const credential = await signInWithPopup(firebaseAuth, provider);
  const idToken = await credential.user.getIdToken();
  return {
    idToken,
    email: credential.user.email,
    displayName: credential.user.displayName,
  };
}

/**
 * Clears the Firebase client session. The authoritative sign-out is the server
 * deleting the session cookie — this only stops the next popup from silently
 * reusing the previous Google account.
 */
export async function signOutOfFirebase(): Promise<void> {
  if (!firebaseConfigured) return;
  try {
    await firebaseSignOut(getFirebaseAuth());
  } catch {
    // Never let a client-side cleanup failure block the real sign-out.
  }
}
